import {
  isApiV2ContextReady as isScopedApiV2ContextReady,
  type ApiV2QueryContext,
} from "./apiV2QueryScope";
import {
  getDefaultQueryOptions as getSharedDefaultQueryOptions,
  IDEMPOTENCY_KEY_HEADER,
} from "@sellar-io/common";
import {
  BusinessGetResponseType,
  ErrorSchemaType,
} from "@sellar-io/api-v2/types";
import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  httpRequest,
  HttpError,
  HttpResponse,
  HttpMethod,
  isHttpError,
} from "~/utils/httpClient";
import { simulateSessionRequestFailure } from "../../sessionTestControls";
import { ExtendedFirebaseUser } from "../../useFirebaseUser.shared";
import { frontendVersionHeaders } from "../../../buildVersion";
import { InvalidApiResponseError } from "../../../utils/apiResponseDiagnostics";

export type ApiOrderParams = [[string, "asc" | "desc"]];

export type ApiV2ListParams = {
  where?: {
    [key: string]: string | number;
  };
  search?: string;
  order?: ApiOrderParams;
  offset?: number;
  limit?: number;
};

export type ApiV2ContextOptions = {
  requireBusiness?: boolean;
  requireUser?: boolean;
  /** Require the current user's membership, including when they are an admin. */
  requireBusinessMembership?: boolean;
};

type ApiV2QueryBaseOptions<TQueryFnData = unknown, TError = HttpError> = {
  requireUser?: never;
  requireBusiness?: never;
  requireBusinessMembership?: never;
  requireBusinessType?: never;
  scope?: never;
  context?: never;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  retry?: UseQueryOptions<TQueryFnData, TError>["retry"];
  staleTime?: number;
  onError?: (error: TError) => void;
  RetailerId?: number;
};

export const hasBusinessMembership = ({
  user,
  business,
}: {
  user: ExtendedFirebaseUser | undefined;
  business: BusinessGetResponseType | null;
}) => !!user && !!business?.id && !!business.BusinessUser;

const getBaseQueryOptions = <TQueryFnData = unknown, TError = HttpError>(
  overrideOptions?: ApiV2QueryBaseOptions<TQueryFnData, TError>,
): ApiV2QueryBaseOptions<TQueryFnData, TError> =>
  getSharedDefaultQueryOptions<
    TError,
    ApiV2QueryBaseOptions<TQueryFnData, TError>
  >(overrideOptions);

export const isApiV2ContextReady = ({
  user,
  business,
  overrideOptions,
}: {
  user: ExtendedFirebaseUser | undefined;
  business: BusinessGetResponseType | null;
  overrideOptions?: ApiV2ContextOptions;
}): boolean => {
  const requireBusiness = overrideOptions?.requireBusiness ?? true;
  const requireUser = overrideOptions?.requireUser ?? true;

  const isUserReady = !requireUser || !!user;
  const isBusinessReady = !requireBusiness || !!business?.id;
  const isMembershipReady =
    !overrideOptions?.requireBusinessMembership ||
    hasBusinessMembership({ user, business });

  return isUserReady && isBusinessReady && isMembershipReady;
};

type TransitionalQueryOptions<TOptions> = Omit<
  TOptions,
  keyof ApiV2ContextOptions
> &
  ApiV2ContextOptions;

const getBaseQueryOptionsV2 = <TQueryFnData = unknown, TError = HttpError>({
  user,
  business,
  overrideOptions,
}: {
  user: ExtendedFirebaseUser | undefined;
  business: BusinessGetResponseType | null;
  overrideOptions?: TransitionalQueryOptions<
    ApiV2QueryBaseOptions<TQueryFnData, TError>
  >;
}): ApiV2QueryBaseOptions<TQueryFnData, TError> => {
  const {
    requireUser: _requireUser,
    requireBusiness: _requireBusiness,
    requireBusinessMembership: _requireMembership,
    ...options
  } = overrideOptions ?? {};
  return getSharedDefaultQueryOptions<
    TError,
    ApiV2QueryBaseOptions<TQueryFnData, TError>
  >(options, isApiV2ContextReady({ user, business, overrideOptions }));
};

export interface ApiV2QueryOptions<
  TQueryFnData = unknown,
  TError = HttpError,
> extends ApiV2QueryBaseOptions<TQueryFnData, TError> {
  refetchInterval?: UseQueryOptions<TQueryFnData, TError>["refetchInterval"];
  placeholderData?: UseQueryOptions<TQueryFnData, TError>["placeholderData"];
}

/**
 * @deprecated Use getDefaultQueryOptionsV2 instead. This legacy version does not automatically
 * handle the 'enabled' state based on user/business availability.
 */
export const getDefaultQueryOptions = <
  TQueryFnData = unknown,
  TError = HttpError,
>(
  overrideOptions?: ApiV2QueryOptions<TQueryFnData, TError>,
): ApiV2QueryOptions<TQueryFnData, TError> => ({
  ...getBaseQueryOptions<TQueryFnData, TError>(overrideOptions),
  ...overrideOptions,
});

/** Temporary argument compatibility while custom-readiness callers migrate. */
type ScopedQueryOptions<TOptions> = Omit<
  TOptions,
  keyof ApiV2ContextOptions
> & {
  requireUser?: never;
  requireBusiness?: never;
  requireBusinessMembership?: never;
  requireBusinessType?: never;
  scope?: never;
  context?: never;
};

type V2QueryArguments<TOptions> =
  | {
      context: ApiV2QueryContext;
      options?: ScopedQueryOptions<TOptions>;
      user?: never;
      business?: never;
      overrideOptions?: never;
    }
  | {
      user: ExtendedFirebaseUser | undefined;
      business: BusinessGetResponseType | null;
      overrideOptions?: TransitionalQueryOptions<TOptions>;
      context?: never;
      options?: never;
    };

/** Applies scope readiness; the old argument shape is retained for the review PR. */
export const getDefaultQueryOptionsV2 = <
  TQueryFnData = unknown,
  TError = HttpError,
>(
  args: V2QueryArguments<ApiV2QueryOptions<TQueryFnData, TError>>,
): ApiV2QueryOptions<TQueryFnData, TError> => {
  if (args.context !== undefined) {
    return getSharedDefaultQueryOptions<
      TError,
      ApiV2QueryOptions<TQueryFnData, TError>
    >(args.options, isScopedApiV2ContextReady(args.context));
  }
  return getBaseQueryOptionsV2<TQueryFnData, TError>(args);
};

export const generateSellarMetaData = (
  metadata: Record<string, string | number | boolean | null>,
) => {
  return JSON.stringify(metadata);
};

export interface ApiV2InfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = HttpError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
> extends ApiV2QueryBaseOptions<TQueryFnData, TError> {
  placeholderData?: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >["placeholderData"];
  refetchInterval?: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >["refetchInterval"];
  getNextPageParam?: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >["getNextPageParam"];
  refetchIntervalInBackground?: boolean;
  initialPageParam?: TPageParam;
}

/**
 * @deprecated Use getDefaultInfiniteQueryOptionsV2 instead. This legacy version does not automatically
 * handle the 'enabled' state based on user/business availability.
 */
export const getDefaultInfiniteQueryOptions = <
  TQueryFnData = unknown,
  TError = HttpError,
>(
  overrideOptions: ApiV2InfiniteQueryOptions<TQueryFnData, TError> & {
    limit: number | undefined;
  },
) => {
  const baseOptions = getBaseQueryOptions<TQueryFnData, TError>(
    overrideOptions,
  );
  return {
    ...baseOptions,
    getNextPageParam: (lastPage: TQueryFnData, allPages: TQueryFnData[]) => {
      if (Array.isArray(lastPage) && overrideOptions.limit === lastPage.length)
        return (overrideOptions.limit as number) * allPages.length;
      return undefined;
    },
    initialPageParam: 0,
    ...overrideOptions,
  };
};

/** Requires pagination options in either supported argument shape. */
type V2InfiniteQueryArguments<TOptions> =
  | {
      context: ApiV2QueryContext;
      options: ScopedQueryOptions<TOptions>;
      user?: never;
      business?: never;
      overrideOptions?: never;
    }
  | {
      user: ExtendedFirebaseUser | undefined;
      business: BusinessGetResponseType | null;
      overrideOptions: TransitionalQueryOptions<TOptions>;
      context?: never;
      options?: never;
    };

/** Applies scope readiness while custom-readiness callers migrate separately. */
export const getDefaultInfiniteQueryOptionsV2 = <
  TQueryFnData = unknown,
  TError = HttpError,
>(
  args: V2InfiniteQueryArguments<
    ApiV2InfiniteQueryOptions<TQueryFnData, TError> & {
      limit: number | undefined;
    }
  >,
) => {
  const rawOptions =
    args.context !== undefined ? args.options : args.overrideOptions;
  const {
    requireUser: _requireUser,
    requireBusiness: _requireBusiness,
    requireBusinessMembership: _requireMembership,
    ...options
  } = rawOptions;
  const ready =
    args.context !== undefined
      ? isScopedApiV2ContextReady(args.context)
      : isApiV2ContextReady(args);
  const baseOptions = getSharedDefaultQueryOptions<
    TError,
    ApiV2InfiniteQueryOptions<TQueryFnData, TError>
  >(options, ready);
  return {
    ...baseOptions,
    getNextPageParam: (lastPage: TQueryFnData, allPages: TQueryFnData[]) => {
      if (
        options.limit !== undefined &&
        Array.isArray(lastPage) &&
        options.limit === lastPage.length
      )
        return options.limit * allPages.length;
      return undefined;
    },
    initialPageParam: 0,
    ...options,
    enabled: baseOptions.enabled,
  };
};

export enum ApiV2RequestScope {
  Public = "public",
}

export type SellarApiV2RequestPromiseFactory = {
  path: string;
  method: HttpMethod;
  responseType?: "json" | "text";
  params?: ApiV2ListParams | unknown;
  data?: unknown;
  sellarMetadata?: string;
  idempotencyKey?: string;
  timeout?: number;
  signal?: AbortSignal;
} & (
  | {
      scope: ApiV2RequestScope.Public;
      user?: never;
      business?: never;
      retailerId?: never;
      headers?: never;
    }
  | {
      // Preserve existing callers until their endpoint scopes are audited.
      scope?: undefined;
      user?: ExtendedFirebaseUser;
      business: BusinessGetResponseType | null;
      retailerId?: number;
      headers?: Record<string, string>;
    }
);

export const apiV2AssertResponse = <
  TData = unknown,
  TResponse = { data: TData },
>(
  response: HttpResponse<unknown>,
): TResponse => {
  if (
    typeof response.data !== "object" ||
    response.data === null ||
    !("data" in response.data)
  )
    throw new InvalidApiResponseError(response);

  return response.data as TResponse;
};

export const apiV2AssertError = (error: unknown) => {
  if (
    isHttpError<unknown>(error) &&
    typeof error.response?.data === "object" &&
    error.response.data !== null &&
    "message" in error.response.data &&
    typeof error.response.data.message === "string"
  ) {
    return error as HttpError<{ message: string; details: ErrorSchemaType }>;
  }

  console.error("Something went wrong...");
  console.error(error);

  return undefined;
};

export const apiV2Request = async ({
  scope,
  user,
  business,
  retailerId,
  method,
  path,
  params = {},
  data = null,
  headers,
  idempotencyKey,
  sellarMetadata,
  timeout,
  signal,
  responseType,
}: SellarApiV2RequestPromiseFactory) => {
  simulateSessionRequestFailure(path, method);
  let token: string | undefined;
  if (scope !== ApiV2RequestScope.Public && user) {
    token = await user.firebaseUser.getIdToken();
  }
  return httpRequest<unknown>({
    method,
    timeout,
    signal,
    responseType,
    params,
    data,
    url: `${import.meta.env.VITE_API_V2_URL}/${path}`,
    headers: {
      sellarMetadata,
      "x-sellar-client": "frontend",
      ...(idempotencyKey && { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey }),
      ...(token && { authorization: `Bearer ${token}` }),
      ...(token &&
        business &&
        business.Retailer && { RetailerId: business.Retailer.id.toString() }),
      //used for suppliers acting on behalf of retailers
      ...(token && business && retailerId && { RetailerId: retailerId }),
      ...(token &&
        business &&
        business.Supplier && { SupplierId: business.Supplier.id.toString() }),
      ...(scope !== ApiV2RequestScope.Public && headers),
      ...frontendVersionHeaders,
    },
  });
};
